import { parseDate } from 'js/utils/model';

const SYNTHETIC_PARTICIPANT_ID = 1;

function defaultScorableAnswers() {
  return [
    { value: '0', is_na: false },
    { value: '1', is_na: false },
  ];
}

function rubricQuestionStableKey(q) {
  const k = q.key;
  if (k != null && String(k).trim() !== '') return k;
  const g = q.guid;
  if (g != null && String(g).trim() !== '') return g;
  return q.id;
}

function rubricCellKey(qsKey, question) {
  return `${qsKey}|${question.key}`;
}

function applyCarryScoresForward(sortedAssessments) {
  const latestResponsesByCell = new Map();

  const carriedAssessments = sortedAssessments.map((assessment) => {
    const instanceTime = assessment.inserted_at;

    const question_sets = assessment.question_sets.map((qs) => {
      const qsKey = qs.key;
      const questions = qs.questions.map((q) => {
        const cellKey = rubricCellKey(qsKey, q);
        const hasResponse = q.responses.length > 0;

        if (hasResponse) {
          latestResponsesByCell.set(cellKey, q);
          return q;
        }

        const carried = latestResponsesByCell.get(cellKey);
        if (!carried?.responses?.length) {
          return q;
        }

        return {
          ...carried,
        };
      });

      const qsInsertedAt = questions.reduce((acc, q) => {
        return q.inserted_at < acc ? q.inserted_at : acc;
      }, instanceTime);

      return { ...qs, inserted_at: qsInsertedAt, questions };
    });

    return { ...assessment, question_sets };
  });

  return carriedAssessments;
}

export function transformActivityToAssessments(
  questionSets,
  assessmentInstances,
  assessmentScoreMaps,
  carryScoresForward = true
) {
  if (!Array.isArray(questionSets) || !Array.isArray(assessmentInstances)) {
    return [];
  }

  const maps = assessmentScoreMaps || {};

  const sorted = assessmentInstances
    .map((instance) => {
      const assessmentId = instance.assessment_id ?? instance.id;
      const assessmentKey = instance.assessment_key ?? instance.key ?? '';
      const instanceTime = parseDate(instance.inserted_at);
      const scoreMap = maps[String(assessmentId)] ?? maps[assessmentId] ?? {};

      const question_sets = questionSets.map((qs) => {
        const qsKey = qs.key;

        const questions = (qs.questions || []).map((q) => {
          const rubricQuestionKey = rubricQuestionStableKey(q);
          const sources = q.sources || [];
          const source = sources.find(
            (s) => s.assessment_key === assessmentKey
          );

          // Lookup uses **source** keys (assessment document), matching API triple map.
          let tripleKey = null;
          if (source && assessmentKey) {
            const qk = source.key ?? source.question_key;
            const qsk = source.question_set_key;
            if (qk != null && qsk != null) {
              tripleKey = `${assessmentKey}|${qsk}|${qk}`;
            }
          }

          const raw =
            tripleKey != null && scoreMap[tripleKey] != null
              ? scoreMap[tripleKey]
              : null;
          const score =
            raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;

          const responses =
            score != null && source
              ? [
                  {
                    participant_id: SYNTHETIC_PARTICIPANT_ID,
                    value: String(score),
                    is_na: false,
                    inserted_at: instanceTime,
                  },
                ]
              : [];

          return {
            id: q.guid || q.id || `${qsKey}:${rubricQuestionKey}`,
            key: rubricQuestionKey,
            name: q.name,
            text: q.text,
            type: q.type || 'likert',
            inserted_at: new Date(instanceTime),
            answers: defaultScorableAnswers(),
            responses,
            ...(q.weight != null && q.weight !== ''
              ? { weight: q.weight }
              : {}),
          };
        });

        return {
          key: qsKey,
          name: qs.name,
          inserted_at: new Date(instanceTime),
          category: qs.category || 'uncategorized',
          questions,
          ...(qs.weight != null && qs.weight !== ''
            ? { weight: qs.weight }
            : {}),
        };
      });

      return {
        id: assessmentId,
        inserted_at: new Date(instanceTime),
        participants: [{ id: SYNTHETIC_PARTICIPANT_ID, cohort: null }],
        config: { categories: {} },
        question_sets,
      };
    })
    .sort((a, b) => a.inserted_at - b.inserted_at);

  if (!carryScoresForward) {
    return sorted;
  }

  return applyCarryScoresForward(sorted);
}
